import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  EMPTY_DRAFT,
  FIELD_ORDER,
  computeDraftErrors,
  draftFromRow,
  fieldElementId
} from '../utils/beneficiary-draft.utils';

import type {
  BeneficiaryRow,
  DraftBeneficiary,
  DraftErrorCode,
  DraftField
} from '../types/beneficiary-draft.types';
import type {KeyboardEvent} from 'react';

/**
 * Draft errors reuse the shared `common:*` vocabulary — see Phase 2 field-vocabulary consolidation.
 * `invalid_national_id`/`invalid_company_id` are format+checksum buckets, so they map to
 * `common:idValidation.*` messages rather than beneficiary-specific copy.
 */
const DRAFT_ERROR_KEYS: Record<DraftErrorCode, string> = {
  required: 'common:required',
  invalid_national_id: 'common:idValidation.invalid_national_id_checksum',
  invalid_company_id: 'common:idValidation.invalid_company_id_format',
  future_date: 'common:futureDateNotAllowed'
};

interface UseBeneficiaryDraftFormParams {
  beneficiaries: BeneficiaryRow[];
  isAddingParty?: boolean;
  editingPartyId?: string | null;
  onConfirmDraft?: (draft: DraftBeneficiary) => void;
  onConfirmEdit?: (partyId: string, values: DraftBeneficiary) => void;
  onCancelDraft?: () => void;
  onCancelEdit?: () => void;
}

export function useBeneficiaryDraftForm({
  beneficiaries,
  isAddingParty,
  editingPartyId,
  onConfirmDraft,
  onConfirmEdit,
  onCancelDraft,
  onCancelEdit
}: UseBeneficiaryDraftFormParams) {
  const {t} = useTranslation('trustAccounts');
  const [formValues, setFormValues] = useState<DraftBeneficiary>(EMPTY_DRAFT);
  const [erroredFields, setErroredFields] = useState<Set<DraftField>>(new Set());

  useEffect(() => {
    setErroredFields(new Set());
    if (isAddingParty) {
      setFormValues(EMPTY_DRAFT);
      return;
    }
    if (editingPartyId) {
      const row = beneficiaries.find(b => b.partyId === editingPartyId);
      if (row) setFormValues(draftFromRow(row));
      return;
    }
    setFormValues(EMPTY_DRAFT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingParty, editingPartyId]);

  const rows = useMemo<BeneficiaryRow[]>(() => {
    if (isAddingParty) {
      return [...beneficiaries, {...formValues, source: 'manual', isDraft: true}];
    }
    if (editingPartyId) {
      return beneficiaries.map(row =>
        row.partyId === editingPartyId ? {...row, ...formValues, isEditing: true} : row
      );
    }
    return beneficiaries;
  }, [beneficiaries, isAddingParty, editingPartyId, formValues]);

  const updateField = (field: DraftField, value: DraftBeneficiary[DraftField]) => {
    setFormValues(v => ({...v, [field]: value}) as DraftBeneficiary);
  };

  const attemptSave = (row: BeneficiaryRow) => {
    const draft = draftFromRow(row);
    const errors = computeDraftErrors(draft);
    const invalidFields = Object.keys(errors) as DraftField[];
    if (invalidFields.length > 0) {
      setErroredFields(new Set(invalidFields));
      const firstInvalid = FIELD_ORDER.find(field => invalidFields.includes(field));
      if (firstInvalid) document.getElementById(fieldElementId(firstInvalid))?.focus();
      return;
    }
    if (row.isDraft) {
      onConfirmDraft?.(draft);
    } else if (row.partyId) {
      onConfirmEdit?.(row.partyId, draft);
    }
  };

  const handleDraftKeyDown = (row: BeneficiaryRow) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      attemptSave(row);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      (row.isDraft ? onCancelDraft : onCancelEdit)?.();
    }
  };

  const errorMessage = (code: DraftErrorCode | undefined): string | undefined =>
    code ? t(DRAFT_ERROR_KEYS[code]) : undefined;

  return {rows, erroredFields, updateField, attemptSave, handleDraftKeyDown, errorMessage};
}
